/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Query } from 'react-apollo';

import gql from 'graphql-tag';
import debounce from 'lodash/debounce';

import helpCenterLinks from 'js/lib/courseraHelpcenter';

import type { Theme } from '@coursera/cds-core';
import { Link, Typography2 } from '@coursera/cds-core';

import katexify from 'bundles/author-common/utils/katex';

import _t from 'i18n!nls/compound-assessments';

export type LatexData = {
  message: string;
  warnings: Array<string>;
  success: boolean;
};

/* eslint-disable graphql/template-strings */
const LatexQuery = gql`
  query LatexQuery($userText: String!, $skip: Boolean!) {
    latexData(userText: $userText)
      @skip(if: $skip)
      @rest(type: "LatexData", path: "assess/v1/latex?{args}", method: "GET") {
      message
      warnings
      success
    }
  }
`;
/* eslint-enable graphql/template-strings */

type MathExpressionPreviewViewProps = {
  latexData: LatexData | null;
  isLatexDataLoading: boolean;
};

const styles = {
  root: (theme: Theme) =>
    css({
      padding: 'var(--cds-spacing-200)',
      marginBottom: 'var(--cds-spacing-150)',
      backgroundColor: 'var(--cds-color-grey-25)',
    }),
  mathExpressionPreview: (theme: Theme) =>
    css({
      paddingLeft: 'var(--cds-spacing-200)',
    }),
};

export const MathExpressionPreviewView: React.FC<MathExpressionPreviewViewProps> = ({
  latexData,
  isLatexDataLoading,
}) => (
  <div css={styles.root} role="alert">
    <Typography2 component="p" variant="bodyPrimary">
      {isLatexDataLoading && _t('Preview updating...')}
      {!isLatexDataLoading && !latexData && _t('Preview will appear here...')}
      {latexData && !latexData.success && (
        <React.Fragment>
          {_t('Preview unable to update: ')}
          <Link target="_blank" rel="noopener noreferrer" href={helpCenterLinks.getMathAssignmentLink()}>
            {latexData.message}
          </Link>
        </React.Fragment>
      )}
    </Typography2>

    {latexData && latexData.success && !isLatexDataLoading && latexData.message && (
      <div
        css={styles.mathExpressionPreview}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: katexify('$$' + latexData.message + '$$') }}
      />
    )}

    {latexData && latexData.warnings && latexData.warnings.length > 0 && (
      <Typography2 variant="bodyPrimary" component="div">
        {latexData.warnings.map((msg) => (
          <div key={msg}>{msg}</div>
        ))}
      </Typography2>
    )}
  </div>
);

type Props = {
  userText?: string | null;
};

class MathExpressionPreview extends React.Component<Props> {
  // the following function debounce the component update
  // because there might be a lot of changes to userText if the user is typing
  // it is to prevent unnecessary network requests
  shouldComponentUpdate() {
    this.debouncedUpdate();
    return false;
  }

  debouncedUpdate = debounce(this.forceUpdate, 500);

  render() {
    const { userText } = this.props;
    return (
      <Query
        query={LatexQuery}
        variables={{
          userText,
          skip: !userText,
        }}
      >
        {({ loading, data }: $TSFixMe) => (
          <MathExpressionPreviewView isLatexDataLoading={loading} latexData={(data || {}).latexData} />
        )}
      </Query>
    );
  }
}

export default MathExpressionPreview;
